import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from 'shared/environments/environment';
import type { BookDto } from 'shared/src/types/book-dto';

export type CurrencyCode = 'USD' | 'EUR' | 'PLN' | 'GBP' | 'INR' | string;

export type BillingPeriodType = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';

export interface Product {
  id: string;
  code: string;
  billingPeriodAmount: number;
  billingPeriodType: BillingPeriodType;

  trialDays: number;

  compareAtPrice: number;

  recurringPrice: number;
  dailyPrice: number;

  currency: CurrencyCode;

  introductoryRecurringPrice: number;
  introductoryDailyPrice: number;

  setupFeePrice: number;

  isDefault: boolean;

  discount?: Discount;

  firstPayment: number;
}

export interface SubscriptionState {
  isSubscribed: boolean;
  isActivePromo: boolean;
  willRenew?: boolean;
  expireDate: Date | null;
  type: 'ADAPTY' | 'STRIPE';
  quotas?: Record<string, number>;
}

export interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface UserDetails extends User {
  isEligibleForTrial: boolean;
  subscriptionState: SubscriptionState;
  webProduct?: Product;
}

export type Placement =
  | 'email_discount'
  | 'holiday'
  | 'growth_quiz'
  | 'growth_quiz_discount'
  | 'google_quiz_2'
  | 'google_quiz_2_discount'

  //applovin traffic:
  | 'applovin'
  | 'applovin_discount'

  //regular flow:
  | 'upsell_productivity_summaries'
  | 'upsell_leadership_summaries'
  | 'upsell_leadership_productivity_summaries'
  | 'upsell_voice_presets_discount'

  //main flow (after a summaries purchase):
  | 'upsell_voice_presets'

  //feature funnels (google / short / kindle / romance) - x1.5 won the Aug 2026 price test:
  | 'upsell_voice_presets_x1.5'
  | 'upsell_speed_x1.5'
  | 'upsell_voice_presets_speed_x1.5'

  //credits top-up paywall:
  | 'credits_topup';

export interface Discount {
  amount: number;
  units: CurrencyCode | 'PERCENTAGE';
  duration?: string;
}

export interface CreateCustomerInput {
  email: string;
  name?: string;
  amplitudeDeviceId?: string;
  webQuizAnswers?: { [key: string]: unknown };
  selectedBooks?: BookDto[];
  language?: string;
}

export interface StripePurchaseInput {
  productId: string;
  email?: string;
  customerId?: string;
  name?: string; //always empty now
  amplitudeDeviceId?: string;
}

export interface StripeBootstrapResponse {
  publishableKey: string;
}

export interface Addon {
  id: string;
  code: string;
  price: number;
  compareAtPrice: number;
  currency: CurrencyCode;
  discount?: Discount;
}

export interface BuyAddonInput {
  email?: string;
  stripeCustomerId?: string;
  addonCode: string;
  placement: Placement;
  language: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl = environment.serviceUrl;

  constructor(private http: HttpClient) {}

  getProducts(placement: Placement) {
    const url = `${this.baseUrl}/products?placement=${placement}`;

    return this.http
      .get<{ products: Product[] }>(url, {
        withCredentials: true,
      })
      .pipe(
        map(response => response.products),
        map(products => {
          // TEMP WORKAROUND: Override pricing
          return products.map(product => {
            if (product.code.includes('peech.web.yearly.premium.addon-summaries')) {
              return {
                ...product,
                compareAtPrice: 199.99,
                discount: {
                  amount: 80,
                  units: 'PERCENTAGE',
                  duration: 'ONCE',
                },
              };
            }

            return product;
          });
        })
      );
  }

  createStripeCustomer(data: CreateCustomerInput) {
    return this.http.post<{ customerId: string }>(`${this.baseUrl}/stripe/create-customer`, data, {
      withCredentials: true,
    });
  }

  getStripeBootstrap() {
    return this.http.get<StripeBootstrapResponse>(`${this.baseUrl}/stripe/bootstrap`, {
      withCredentials: true,
    });
  }

  getAddons(placement: Placement) {
    return this.http.get<{ addons: Addon[] }>(`${this.baseUrl}/addons`, {
      params: new HttpParams().set('placement', placement),
      withCredentials: true,
    });
  }

  createStripeSubscription(data: StripePurchaseInput) {
    return this.http.post<{
      clientSecret: string;
      isSetupIntent: boolean;
      stripeCustomerId: string;
    }>(`${this.baseUrl}/stripe/create-subscription`, data, {
      withCredentials: true,
    });
  }

  cancelStripeSubscription() {
    return this.http.post(`${this.baseUrl}/stripe/cancel-subscription`, null);
  }

  buyStripeUpsell(data: BuyAddonInput) {
    return this.http.post<{
      stripeCustomerId: string;
    }>(`${this.baseUrl}/stripe/addons/buy`, data, {
      withCredentials: true,
    });
  }

  getUserDetails() {
    return this.http.get<UserDetails>(`${this.baseUrl}/users/details`, {
      withCredentials: true,
    });
  }

  identifyUser(id: string | undefined, email: string | undefined) {
    let params = new HttpParams();

    if (id) {
      params = params.set('id', id);
    }

    if (email) {
      params = params.set('email', email);
    }

    return this.http.get<User>(`${this.baseUrl}/users/identify`, { params });
  }

  sendEmailLoginLink(email: string, redirectUrl: string) {
    return this.http.post(`${environment.gatewayUrl}/magicLink`, {
      email,
      redirectUrl,
      platform: 'web',
    });
  }

  getAppstoreDeepLink(
    email: string | undefined,
    stripeCustomerId: string | undefined,
    redirectUrl: string
  ) {
    return this.http.post<{ jwtToken: string; deepLink: string }>(
      `${this.baseUrl}/deep-link/generate`,
      {
        email,
        stripeCustomerId,
        platform: 'ios',
        redirectUrl,
      }
    );
  }
}
